import React, { useEffect, useState, useCallback } from 'react';
import { useMutation, useQuery } from 'apollo-3';

import { nsMarkNotificationAsRead } from 'GraphQL/mutation/Notification.mutation';
import { mapNotifications } from 'Components/Notification/utils';
import useMyExternalProfile from 'Routes/Classroom/Domain/useMyExternalProfile';
import { nsNotificationsCount, nsNotifications } from 'GraphQL/query/Notification.query';
import { useConfig } from 'Context/ConfigContext';
import { ANNOUNCEMENT_TYPE } from 'Routes/MyCourses/Domain/constants';

import { announcements as queryAnnouncements } from '../../GraphQL/query/Announcement.query';

import { AnnouncementContext } from './context';

const useMarkNotificationAsRead = () => {
  const [markNotificationAsReadMutation, { data }] = useMutation(nsMarkNotificationAsRead);

  return {
    markNotificationAsReadMutation,
    markNotifications: data ? mapNotifications(data.nsMarkNotificationAsRead.notifications) : [],
  };
};

const AnnouncementProvider = ({ children }) => {
  const { myCourses } = useConfig();
  const { markNotificationAsReadMutation } = useMarkNotificationAsRead();
  const { data: myExternalProfileData } = useMyExternalProfile({ fetchPolicy: 'cache-only' });
  const [announcements, setAnnouncements] = useState([]);

  // NOTE: Limit and offset for query announcements
  const limit = 10;
  const offset = 0;
  const [hasMoreAnnouncements, setHasMoreAnnouncements] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const providerName = myExternalProfileData?.myExternalProfile?.providerName ?? '';
  const enableFeatureAnnouncementByBU = myCourses?.enableFeatureAnnouncementByBU ?? [];

  const {
    data: dataAnnouncements,
    loading: loadingAnnouncements,
    fetchMore,
  } = useQuery(queryAnnouncements, {
    variables: { type: ANNOUNCEMENT_TYPE.NOTIFICATION, limit, offset },
    skip: !providerName || !enableFeatureAnnouncementByBU.includes(providerName),
    fetchPolicy: 'cache-and-network',
  });

  const { data: dataCount, loading: loadingCount } = useQuery(nsNotificationsCount, {
    variables: { filter: 'ALL' },
    fetchPolicy: 'cache-and-network',
  });

  const count = dataCount?.nsNotificationsCount?.count ?? 0;

  const { data: dataNotifications, loading: loadingNotification } = useQuery(nsNotifications, {
    variables: { first: count },
    fetchPolicy: 'cache-and-network',
    skip: count === 0,
  });

  const handleMarkNotificationAsRead = async (ids) => {
    markNotificationAsReadMutation({ variables: { ids } });
  };

  // NOTE: Load more for query announcements
  const loadMoreAnnouncements = useCallback(() => {
    if (loadingAnnouncements || isFetchingMore || !hasMoreAnnouncements) {
      return;
    }

    setIsFetchingMore(true);
    const newOffset = dataAnnouncements?.announcements?.length ?? 0;

    fetchMore({
      variables: {
        offset: newOffset,
        limit,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        setIsFetchingMore(false);

        if (!fetchMoreResult) return prev;

        const newAnnouncements = fetchMoreResult.announcements ?? [];
        if (newAnnouncements.length < limit) {
          setHasMoreAnnouncements(false);
        }

        return {
          ...prev,
          announcements: [...prev.announcements, ...newAnnouncements],
        };
      },
    });
  }, [
    loadingAnnouncements,
    isFetchingMore,
    hasMoreAnnouncements,
    dataAnnouncements?.announcements?.length,
    fetchMore,
  ]);

  useEffect(() => {
    if (loadingAnnouncements || loadingCount || loadingNotification) {
      return;
    }

    const announcementsList = dataAnnouncements?.announcements ?? [];
    const notificationsList = dataNotifications?.nsNotifications?.notifications ?? [];
    const notifications = mapNotifications(notificationsList);

    if (offset === 0 && announcementsList.length < limit) {
      setHasMoreAnnouncements(false);
    }

    // NOTE: Combine announcements and notifications
    const announcementWithNotifications = announcementsList.concat(notifications);
    setAnnouncements(announcementWithNotifications);
  }, [
    dataAnnouncements?.announcements,
    dataNotifications?.nsNotifications?.notifications,
    loadingAnnouncements,
    loadingCount,
    loadingNotification,
    offset,
  ]);

  return (
    <AnnouncementContext.Provider
      value={{
        announcements,
        markNotificationAsRead: handleMarkNotificationAsRead,
        loading: loadingAnnouncements || loadingCount || loadingNotification || isFetchingMore,
        hasMoreAnnouncements,
        loadMoreAnnouncements,
      }}
    >
      {children}
    </AnnouncementContext.Provider>
  );
};

export default AnnouncementProvider;
